import { PureQueryOptions } from '@apollo/client';
import { REPORT_LINK, getLocalizedDateWithoutLeadingZeroes } from '@arnold/core';
import { TFunction } from 'react-i18next';
import { getFullName, getTopicGroupName, showResult } from '../../lib/helpers';
import i18n from '../../translations/i18n';
import { IsAccessTokenValidQuery, ReportAccessQuery, SequenceReportAccessesQuery } from '../../generated/hooks';
import { ActionButtons } from './ActionButtons';
import { ReportOpenInfo } from './ReportOpenInfo';
import { ReportData, SurveyTableItemType, TableItem } from './types';
import { getStateTitle, getTopicGroup, isSurveyReport } from './utils';
import { BodyMedium, notEmpty, theme, Tooltip } from '@arnold/common';

type GetDataSourceParams = {
  showOpenIcon?: boolean;
  t: TFunction;
  tableItems: TableItem[];
  dependantQueries: Array<string | PureQueryOptions>;
  user: NonNullable<IsAccessTokenValidQuery['isAccessTokenValid']['user']>;
  isReportPendingApproval?: boolean;
};

export const getDataSource = ({
  t,
  showOpenIcon,
  tableItems,
  dependantQueries,
  user,
  isReportPendingApproval,
}: GetDataSourceParams): ReportData[] => {
  return tableItems
    .sort((s1, s2) => s2.timestamp - s1.timestamp)
    .map((tableItem) => {
      const topicGroup = getTopicGroup(
        tableItem.reportBasePath,
        tableItem.type === SurveyTableItemType.Survey ? tableItem.data : undefined,
        tableItem.type === SurveyTableItemType.Sequence
          ? (tableItem.data as NonNullable<SequenceReportAccessesQuery['surveyGroup']>)
          : undefined,
      );

      const sendAt = tableItem.timestamp;

      const reports = tableItem?.data?.reports;
      if (!reports || !Array.isArray(reports)) {
        return [];
      }

      type SurveyType = NonNullable<ReportAccessQuery['survey']>;
      type ReportAccessType = NonNullable<SurveyType['reports']>[0];
      const reportsArray = reports as ReportAccessType[];

      return reportsArray
        .filter((report) => !!report.respondent)
        .sort((a, b) =>
          `${a.respondent?.surname} ${a.respondent?.firstname}`
            .toLowerCase()
            .localeCompare(`${b.respondent?.surname} ${b.respondent?.firstname}`.toLowerCase()),
        )
        .map((report) => {
          const status = getStateTitle(t, report, tableItem.isOngoing);
          const unreadCount = report.sent
            ? (report as NonNullable<SequenceReportAccessesQuery['surveyGroup']>['reports'][0]).unreadCount
            : (report as NonNullable<SequenceReportAccessesQuery['surveyGroup']>['reports'][0]).totalCount;
          const totalCount = (report as NonNullable<SequenceReportAccessesQuery['surveyGroup']>['reports'][0])
            .totalCount;
          return {
            id: report.id,
            reportLabel: (
              <span data-icom={'reports-report-label'}>
                {getFullName(report.respondent?.firstname, report.respondent?.surname, true)}
              </span>
            ),
            status: (
              <BodyMedium
                color={report.sent ? theme.colors.emotionSuccess.default : theme.colors.emotionNeutral.default}
              >
                {status}
              </BodyMedium>
            ),
            reportOpened: <ReportOpenInfo t={t} report={report} showOpenIcon={showOpenIcon} status={status} />,
            actions: (
              <ActionButtons
                report={report}
                reportBasePath={tableItem.reportBasePath}
                t={t}
                isOngoing={tableItem.isOngoing}
                isReportPendingApproval={isReportPendingApproval}
                dependantQueries={dependantQueries}
                user={user}
              />
            ),
            topicName: topicGroup ? getTopicGroupName(i18n.language, topicGroup) : '',
            sendAt: sendAt && getLocalizedDateWithoutLeadingZeroes(new Date(sendAt), i18n.language),
            teams:
              report.teams
                ?.map((team) =>
                  t('team', {
                    team: team.name || t('teams:withoutLeader'),
                  }),
                )
                .join(', ') || '',
            recipients: `${report.respondent?.firstname} ${report.respondent?.surname}`,
            responseRate:
              (isSurveyReport(report) &&
                tableItem.reportBasePath === REPORT_LINK.REPORT &&
                showResult(report?.resultsInfo?.answered, report?.resultsInfo?.total)) ||
              '',
            visitsInLastYear:
              tableItem.type === SurveyTableItemType.Sequence
                ? (report as NonNullable<SequenceReportAccessesQuery['surveyGroup']>['reports'][0]).visitsInLastYear ??
                  undefined
                : undefined,
            read: (
              <Tooltip
                html={
                  notEmpty(unreadCount) && notEmpty(totalCount) ? (
                    <div style={{ whiteSpace: 'pre-line' }}>
                      {t('readMessages', { count: totalCount! - unreadCount!, totalCount: totalCount! })}
                    </div>
                  ) : undefined
                }
              >
                {notEmpty(unreadCount) && notEmpty(totalCount) && (
                  <BodyMedium
                    color={
                      !report.sent
                        ? theme.colors.text.disabled
                        : unreadCount > 0
                          ? theme.colors.emotionDanger.default
                          : theme.colors.emotionSuccess.default
                    }
                  >{`${totalCount - unreadCount}/${totalCount}`}</BodyMedium>
                )}
              </Tooltip>
            ),
          };
        });
    })
    .flat();
};
